/**
 * Tanjong Life · "Commission an integrity sentinel that watches the claims book between the runs."
 *
 * Born on stage from the Home wizard with a sentence that is not a builder intent. The
 * conversation says what it will watch — the settlement release window, the age of the
 * indicator set, the payee re-reads — offers the five authorities it could be given with
 * NONE pre-selected, takes none, ticks the systems the org graph already connects, and
 * deploys the agent bundle (loaded in its own chunk). The agent page then boots its stream
 * from the boot-asset registry keyed `org:pk`.
 *
 * Keywords: `integrity sentinel` is the span; the other two catch a paraphrase. Registered
 * AFTER the chats, the run and the applies and BEFORE the five builds — see the order note
 * in `scenarios/index.ts`.
 */
import type { Scenario } from '../director.js';
import {
  CLAIMS_AUTHORITIES,
  type CommissionAsset,
  makeCommission,
  once,
} from './commission-scenario.js';
import { MAKE_INTEGRITY_SENTINEL } from './tanjong-make-intents.js';

const load = once(() =>
  import('../assets/tanjong-claims-integrity-sentinel.json').then(
    (m) => m.default as unknown as CommissionAsset,
  ),
);

export const tanjongCommissionSentinel: Scenario = makeCommission({
  id: 'tanjong-commission-sentinel',
  keywords: ['integrity sentinel', 'watches the claims book', 'commission an integrity sentinel'],
  intents: [MAKE_INTEGRITY_SENTINEL],
  pk: 'claims-integrity-sentinel',
  name: 'Claims Integrity Sentinel',
  load,
  thinking: [
    {
      thought:
        'The four workflows each own a claim from arrival to disposal. What nobody owns is what becomes true afterwards — between the runs. That is the job, so the probes come from the gaps the desk already admits to.',
    },
    {
      tool: {
        name: 'processes.read',
        input: 'org=tanjong-life · tl-pr11 tl-pr13 tl-pr14 · gates and windows',
        output:
          'tl-pr14: authorise → release window, payee re-read between the gates · tl-pr13: adviser gate with no send timer, flags age · tl-pr11: requirement-set version stamped per file',
      },
    },
    {
      tool: {
        name: 'knowledge.read',
        input: 'base=Claims requirement sets · Singapore · FRAUD-IND-SET',
        output: 'v2 effective 1 June, ingested 6 July · 35 days between the two dates',
      },
    },
    {
      thought:
        'Every one of these is a read. None of them needs an authority, and the honest thing is to be commissioned with none.',
    },
  ],
  watch: {
    text: 'I will commission it as a watcher over the book between the runs — not over any case. Three things to start with. The settlement release window: for every settlement authorised but not yet released, re-read the payee daily and open a case at the treasury lead on any change. The age of the indicator set: for any set re-issued in the last hundred and twenty days, find the claims scored against the version before it. And the lapse flags with no decision: every at-risk flag older than thirty days on which no adviser has either released or declined.',
    sources: ['tl-pr14 · dual control', 'FRAUD-IND-SET-v2 · 35-day gap', 'tl-pr13 · adviser gate'],
  },
  offer:
    'It opens a case at a named person with the evidence attached. It re-scores nothing, holds nothing, releases nothing and messages nobody. Before I deploy it, here are the authorities it COULD be given. I have selected none of them.',
  authorities: CLAIMS_AUTHORITIES,
  refusal:
    'It takes none. Every one of those five is wired on the agent and switched off — 0 of 0, and the action on breach is Halt. Anything it finds goes to a person; nothing it finds moves money, a score or a threshold. If the desk ever wants one of them on, that is a versioned change to the authority matrix that a named person signs, not a setting.',
  // Exactly the three systems the bundle connects: the wizard must never offer
  // a system the agent will not hold, or the Skills tab contradicts the tick.
  appsPrompt:
    'And here is what it may READ. Three systems, every one ticked and every one read-only. Untick any you would rather it never opened.',
  apps: [
    { id: 'claimscore', label: 'Claims administration · FINEOS Claims' },
    { id: 'policyledger', label: 'Policy & benefits · via Pega Customer Service' },
    { id: 'siudesk', label: 'Special investigations case desk' },
  ],
  group: {
    pk: 'financial-crime',
    member: {
      pk: 'claims-integrity-sentinel',
      name: '@Claims Integrity Sentinel',
      role: 'Watches the book between the runs; decides nothing',
      icon: 'radar',
      color: 'var(--green)',
      bg: 'var(--green-tint)',
    },
  },
  kb: { id: 'kb-claims-requirements', scope: 'read · FRAUD-IND-SET' },
  summary:
    'Claims Integrity Sentinel commissioned · five authorities, every one 0 of 0 · Halt · read-only on the systems left ticked',
  commissioned:
    'Commissioned. It is on the Financial Crime desk, it can read the requirement sets, and it is reading the book now — open it and watch it build its probes from what the desk already admits about itself, and hold the one it cannot yet answer honestly.',
  linkLabel: 'Open Claims Integrity Sentinel — watch it start',
});
